import type {
  TeamsRecipientInfo,
  TeamsRecipientType,
  TeamsWithInstallationStatus,
} from '../../components/Automations/teams/types';
import type { CustomWebhookInterface } from '../../components/dashboard/webhooks/WebhooksContext';
import buildURLQuery from '../../lib/buildURLQuery';
import type {
  SlackConnectionStatus,
  TeamsConnectionStatus,
} from '../../reducers/integrationsReducer';
import { useSweepApi } from '../sweep/useSweepApi';
import { useCallback } from 'react';

export type SlackConnectionResponse = {
  status: SlackConnectionStatus;
  upgradeRequired?: boolean;
  includeSandboxes: boolean;
};

export const useDataIntegrationApiFacade = () => {
  const sweepApi = useSweepApi();
  return {
    get_slack_status: useCallback(async (): Promise<SlackConnectionResponse> => {
      const response = await sweepApi.get(`/data-integrations/slack`);
      return response.data;
    }, [sweepApi]),

    patch_slackIncludeSandboxes: useCallback(
      async (includeSandboxes: boolean): Promise<void> => {
        await sweepApi.patch(`/data-integrations/slack`, { includeSandboxes });
      },
      [sweepApi],
    ),

    get_slack_recipients: useCallback(
      async (
        idsArray?: string[],
        invalidateCache?: boolean,
      ): Promise<{ data: SlackRecipient[]; isCached: boolean }> => {
        // the ids and isCached are not sent together
        const response = await sweepApi.get(
          `/data-integrations/slack/recipients${idsArray ? `?${buildURLQuery({ ids: idsArray.join(',') })}` : ''}${invalidateCache ? '?invalidate-cache=true' : ''}`,
        );
        const isCached = !!response.headers['x-sweep-cache'];
        return { data: response.data, isCached };
      },
      [sweepApi],
    ),

    get_custom_integrations: useCallback(async (): Promise<CustomWebhookInterface[]> => {
      const response = await sweepApi.get(`/data-integrations/custom-integrations`);
      return response.data;
    }, [sweepApi]),

    post_custom_integration: useCallback(
      async (integration: CustomWebhookInterface): Promise<CustomWebhookInterface> => {
        const response = await sweepApi.post('/data-integrations/custom-integrations', integration);
        return response.data;
      },
      [sweepApi],
    ),
    put_custom_integration: useCallback(
      async (
        integrationId: string,
        integration: CustomWebhookInterface,
      ): Promise<CustomWebhookInterface> => {
        const response = await sweepApi.put(
          `/data-integrations/custom-integrations/${integrationId}`,
          integration,
        );
        return response.data;
      },
      [sweepApi],
    ),
    delete_custom_integration: useCallback(
      async (integrationId: string, dryRun: boolean = false) => {
        await sweepApi.delete(
          `/data-integrations/custom-integrations/${integrationId}?dry-run=${dryRun}`,
        );
      },
      [sweepApi],
    ),
    get_custom_integration_password: useCallback(
      async (integrationId: string) => {
        const response = await sweepApi.get(
          `/data-integrations/custom-integrations/${integrationId}/reveal-password`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    get_teams_status: useCallback(async (): Promise<TeamsConnectionStatus> => {
      const response = await sweepApi.get(`/data-integrations/teams/connection-status`);
      return response.data;
    }, [sweepApi]),
    disconnect_teams: useCallback(async (): Promise<void> => {
      await sweepApi.delete(`/data-integrations/teams`);
    }, [sweepApi]),
    get_teams_recipients: useCallback(
      async (type?: TeamsRecipientType): Promise<TeamsRecipientInfo[]> => {
        const response = await sweepApi.get(
          `/data-integrations/teams/recipients${type ? `?type=${type}` : ''}`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    get_teams_users_count: useCallback(async (): Promise<number> => {
      const response = await sweepApi.get(`/data-integrations/teams/users/count`);
      return response.data;
    }, [sweepApi]),
    search_teams_recipients: useCallback(
      async (query: string): Promise<TeamsRecipientInfo[]> => {
        const response = await sweepApi.get(
          `/data-integrations/teams/recipients/search?query=${encodeURIComponent(query)}`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    get_teams_channels: useCallback(
      async (teamId: string): Promise<TeamsRecipientInfo[]> => {
        const response = await sweepApi.get(`/data-integrations/teams/channels/${teamId}`);
        return response.data;
      },
      [sweepApi],
    ),
    get_teams_recipient_labels_For_Automation: useCallback(
      async (recipientIds: string[]): Promise<TeamsRecipientInfo[]> => {
        const response = await sweepApi.get(
          `/data-integrations/teams/recipients-by-id?ids=${recipientIds.join(',')}`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    get_teams_users_by_team: useCallback(
      async (teamId: string): Promise<TeamsRecipientInfo[]> => {
        const response = await sweepApi.get(`/data-integrations/teams/users-by-team/${teamId}`);
        return response.data;
      },
      [sweepApi],
    ),

    get_all_teams: useCallback(async (): Promise<TeamsWithInstallationStatus[]> => {
      const response = await sweepApi.get(`/data-integrations/teams/all`);
      return response.data;
    }, [sweepApi]),

    install_teams_app: useCallback(
      async (teams: TeamsRecipientInfo[]): Promise<TeamsWithInstallationStatus[]> => {
        const response = await sweepApi.post(`/data-integrations/teams/install`, { teams });
        return response.data;
      },
      [sweepApi],
    ),

    uninstall_teams_app: useCallback(
      async (teams: TeamsRecipientInfo[]): Promise<TeamsWithInstallationStatus[]> => {
        const response = await sweepApi.post(`/data-integrations/teams/uninstall`, { teams });
        return response.data;
      },
      [sweepApi],
    ),
  };
};
